import { IonSpinner } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useOnMyWay } from '../../hooks/useOrders';

interface OnMyWayButtonProps {
  orderId: string;
}

/**
 * Orderer's "On My Way" signal for a ready_to_pickup order: a pure courtesy
 * heads-up to the chef, rendered ABOVE the pickup code rather than in place of
 * it. It used to gate the verification code, which is why it read as mandatory;
 * the code is ungated now (both parties need it in hand for the handoff
 * verification to mean anything), so this only sends the lister an ETA ping.
 * Idempotent server-side; the mutation invalidates the order so the button
 * disappears on refetch.
 *
 * Callers must NOT render this once `listerConfirmed` is set. Its whole job is
 * buying the chef lead time, and a lister who has pressed Handed Off has none
 * left to use — pressing it there would push "your customer is on their way" at
 * someone already holding an empty container.
 */
export const OnMyWayButton: React.FC<OnMyWayButtonProps> = ({ orderId }) => {
  const { t } = useTranslation('orders');
  const onMyWayMutation = useOnMyWay();
  const busy = onMyWayMutation.isPending;

  const handleClick = () => {
    if (busy) return;
    onMyWayMutation.mutate({ id: orderId });
  };

  return (
    <div className="space-y-2">
      <p className="text-center text-sm text-muted-foreground">
        {t('onMyWay.prompt')}
      </p>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {busy ? <IonSpinner name="crescent" className="h-4 w-4" /> : t('onMyWay.cta')}
      </button>
    </div>
  );
};
